import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';

export interface AircraftType {
  id: number;
  code: string;
  name: string;
  displayName: string;
  description?: string;
  manufacturer: string;
  capacity?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAircraftTypeRequest {
  code: string;
  name: string;
  displayName: string;
  description?: string;
  manufacturer: string;
  capacity?: number;
}

export interface UpdateAircraftTypeRequest {
  code?: string;
  name?: string;
  displayName?: string;
  description?: string;
  manufacturer?: string;
  capacity?: number;
}

export interface AircraftTypeSearchRequest {
  code?: string;
  name?: string;
  manufacturer?: string;
  isActive?: boolean;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

class AircraftTypeService {
  private baseUrl = '/api/aircraft-types';

  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    return {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
  }

  // Получение всех типов самолетов с пагинацией
  async getAircraftTypes(page = 0, size = 20, sortBy = 'id', sortDir = 'ASC'): Promise<PaginatedResponse<AircraftType>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });
    
    const url = getDataApiUrl(`${this.baseUrl}?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение только активных типов самолетов
  async getActiveAircraftTypes(): Promise<AircraftType[]> {
    const url = getDataApiUrl(`${this.baseUrl}/active`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение типа самолета по ID
  async getAircraftTypeById(id: number): Promise<AircraftType> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение типа самолета по коду
  async getAircraftTypeByCode(code: string): Promise<AircraftType> {
    const url = getDataApiUrl(`${this.baseUrl}/code/${encodeURIComponent(code)}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Создание нового типа самолета
  async createAircraftType(aircraftTypeData: CreateAircraftTypeRequest): Promise<AircraftType> {
    const url = getDataApiUrl(this.baseUrl);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(aircraftTypeData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Обновление типа самолета
  async updateAircraftType(id: number, aircraftTypeData: UpdateAircraftTypeRequest): Promise<AircraftType> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(aircraftTypeData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Удаление типа самолета
  async deleteAircraftType(id: number): Promise<void> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  // Поиск типов самолетов
  async searchAircraftTypes(searchData: AircraftTypeSearchRequest, page = 0, size = 20): Promise<PaginatedResponse<AircraftType>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    const url = getDataApiUrl(`${this.baseUrl}/search?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(searchData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение стандартных типов самолетов
  getDefaultAircraftTypes(): CreateAircraftTypeRequest[] {
    return [
      {
        code: "B737",
        name: "Boeing 737",
        displayName: "Boeing 737",
        description: "Узкофюзеляжный пассажирский самолет",
        manufacturer: "Boeing",
        capacity: 189
      },
      {
        code: "A320",
        name: "Airbus A320",
        displayName: "Airbus A320",
        description: "Узкофюзеляжный пассажирский самолет",
        manufacturer: "Airbus",
        capacity: 180
      },
      {
        code: "B777",
        name: "Boeing 777",
        displayName: "Boeing 777",
        description: "Широкофюзеляжный пассажирский самолет",
        manufacturer: "Boeing",
        capacity: 396
      },
      {
        code: "A350",
        name: "Airbus A350",
        displayName: "Airbus A350",
        description: "Широкофюзеляжный пассажирский самолет",
        manufacturer: "Airbus",
        capacity: 350
      }
    ];
  }
}

const aircraftTypeService = new AircraftTypeService();
export default aircraftTypeService; 